import { useState, useEffect, useCallback } from 'react'
import { useIntl } from 'react-intl'
import { addWeeks, format } from 'date-fns'
import { getPTOSuggestions } from '../services'
import { PTOSuggestionsResponse } from '../types'

interface UsePTOSuggestionsProps {
  isFeatureEnabled: boolean
}

interface UsePTOSuggestionsReturn {
  ptoSuggestions: PTOSuggestionsResponse | null
  loading: boolean
  error: string | null
  refetch: () => Promise<void>
  silentRefetch: () => Promise<void>
}

/**
 * Custom hook for fetching PTO suggestions
 * Suggests dates starting from next week, looking 8 weeks ahead
 */
export const usePTOSuggestions = ({ isFeatureEnabled }: UsePTOSuggestionsProps): UsePTOSuggestionsReturn => {
  const { formatMessage } = useIntl()
  const [ptoSuggestions, setPtoSuggestions] = useState<PTOSuggestionsResponse | null>(null)
  const [loading, setPtoLoading] = useState(true)
  const [error, setPtoError] = useState<string | null>(null)

  const fetchPTOSuggestions = useCallback(
    async (silent = false) => {
      if (!isFeatureEnabled) {
        return
      }

      try {
        if (!silent) {
          setPtoLoading(true)
        }
        setPtoError(null)
        // Suggest dates starting from next week
        const fromDate = addWeeks(new Date(), 1)
        const toDate = addWeeks(fromDate, 8)
        const dateFormat = 'yyyy-MM-dd'
        const suggestions = await getPTOSuggestions(format(fromDate, dateFormat), format(toDate, dateFormat))
        setPtoSuggestions(suggestions)
      } catch (err) {
        console.error('Error fetching PTO suggestions:', err)
        setPtoError(formatMessage({ id: 'energyLevel.unableToLoadPTO' }))
      } finally {
        if (!silent) {
          setPtoLoading(false)
        }
      }
    },
    [isFeatureEnabled, formatMessage]
  )

  useEffect(() => {
    fetchPTOSuggestions()
  }, [fetchPTOSuggestions])

  return {
    ptoSuggestions,
    loading,
    error,
    refetch: fetchPTOSuggestions,
    silentRefetch: () => fetchPTOSuggestions(true),
  }
}
